// File de synchronisation de l'application terrain (D-041) : opérations idempotentes (id_client
// UUID v7, version attendue) envoyées dans l'ordre de saisie, reprise après coupure (une opération
// « en cours » au redémarrage est renvoyée, le serveur ignore le doublon), résultats appliqués aux
// objets locaux, photos envoyées une par requête après l'opération qui les référence, repli
// automatique au retour du réseau, déclenchement manuel, téléchargement du travail (toutes les pages).
// Règle de version D-130 : une opération garde la version connue à sa saisie ; seule la version d'une
// opération appliquée ou ignorée en doublon est reportée sur l'objet local, jamais celle d'un conflit.
import type { OperationTerrain } from '@cc/contrats';
import { ErreurApi, messageErreur } from '../api/erreurs';
import { activerSimulateur, apiServeur, basculeAutomatiquePermise, simulateurActif, travailComplet, type ApiTerrain } from './api';
import type { TravailAppareil } from './appareil';
import { nomFichierPhoto } from './photos';
import { simulateurPartage } from './simulateur';
import {
  appliquerVersion,
  blobPhoto,
  compterAttente,
  ecrireHorodatages,
  enfilerOperation,
  enregistrerTravail,
  lireHorodatages,
  lireOperation,
  listerOperations,
  listerPhotos,
  mettreAJourOperation,
  mettreAJourPhoto,
  type CompteurAttente,
  type Horodatages,
  type OperationLocale,
  type PhotoAEnregistrer,
} from './stockage';

type Operation = OperationTerrain;

export type PhaseSynchronisation = 'repos' | 'envoi_operations' | 'envoi_photos' | 'telechargement';

export interface EtatSynchronisation {
  enCours: boolean;
  phase: PhaseSynchronisation;
  enLigne: boolean;
  attente: CompteurAttente;
  horodatages: Horodatages;
  derniereErreur: string | null;
  /** Jeton expiré : la file attend que l'agent rouvre sa session serveur. */
  reconnexionRequise: boolean;
  source: ApiTerrain['source'];
  /** Compteur incrémenté à chaque changement des données locales (relecture par les écrans). */
  revision: number;
}

export interface BilanSynchronisation {
  envoyees: number;
  appliquees: number;
  doublons: number;
  conflits: number;
  rejetees: number;
  photosEnvoyees: number;
  photosEnEchec: number;
  travailTelecharge: boolean;
  erreur: string | null;
  ignoree: 'hors_ligne' | null;
}

export interface OptionsSynchroniseur {
  api?: () => ApiTerrain;
  enLigne?: () => boolean;
  horloge?: () => Date;
  /** Opérations par requête de synchronisation. */
  taillePaquet?: number;
}

const ETATS_RECUS = new Set<OperationLocale['etat']>(['appliquee', 'ignoree_doublon', 'conflit']);

function depouiller(o: OperationLocale): Operation {
  return { id_client: o.id_client, type: o.type, objet_type: o.objet_type, objet_id: o.objet_id, version_attendue: o.version_attendue, enregistre_le: o.enregistre_le, charge: o.charge };
}

export function apiParDefaut(): ApiTerrain {
  return simulateurActif() ? simulateurPartage() : apiServeur;
}

export class Synchroniseur {
  private readonly api: () => ApiTerrain;
  private readonly enLigne: () => boolean;
  private readonly horloge: () => Date;
  private readonly taillePaquet: number;
  private readonly ecouteurs = new Set<(e: EtatSynchronisation) => void>();
  private courant: EtatSynchronisation;
  private promesse: Promise<BilanSynchronisation> | null = null;

  constructor(options: OptionsSynchroniseur = {}) {
    this.api = options.api ?? apiParDefaut;
    this.enLigne = options.enLigne ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
    this.horloge = options.horloge ?? (() => new Date());
    this.taillePaquet = options.taillePaquet ?? 20;
    this.courant = {
      enCours: false,
      phase: 'repos',
      enLigne: this.enLigne(),
      attente: { operations: 0, photos: 0, conflits: 0, rejets: 0 },
      horodatages: { travail_telecharge_le: null, horodatage_serveur: null, derniere_synchronisation: null, dernier_contact_serveur: null },
      derniereErreur: null,
      reconnexionRequise: false,
      source: 'serveur',
      revision: 0,
    };
  }

  etat(): EtatSynchronisation {
    return this.courant;
  }

  abonner(f: (e: EtatSynchronisation) => void): () => void {
    this.ecouteurs.add(f);
    return () => this.ecouteurs.delete(f);
  }

  private changer(partiel: Partial<EtatSynchronisation>): void {
    this.courant = { ...this.courant, ...partiel };
    for (const f of this.ecouteurs) f(this.courant);
  }

  /** Relit compteurs et horodatages dans le stockage et prévient les abonnés. */
  async rafraichirEtat(): Promise<EtatSynchronisation> {
    const [attente, horodatages] = await Promise.all([compterAttente(), lireHorodatages()]);
    this.changer({ attente, horodatages, enLigne: this.enLigne(), source: this.api().source, revision: this.courant.revision + 1 });
    return this.courant;
  }

  /** Enregistre une opération sur l'appareil puis tente aussitôt une synchronisation si le réseau est là. */
  async enfiler(op: Operation, extras: { titre: string; objet_libelle: string; photos?: PhotoAEnregistrer[] }): Promise<OperationLocale> {
    const locale = await enfilerOperation(op, extras);
    await this.rafraichirEtat();
    if (this.enLigne()) void this.synchroniser().catch(() => undefined);
    return locale;
  }

  /** Une seule synchronisation à la fois ; un appel pendant une synchronisation reçoit son bilan. */
  synchroniser(options: { telecharger?: boolean } = {}): Promise<BilanSynchronisation> {
    if (this.promesse) return this.promesse;
    this.promesse = this.executer(options).finally(() => {
      this.promesse = null;
    });
    return this.promesse;
  }

  private async executer(options: { telecharger?: boolean }): Promise<BilanSynchronisation> {
    const bilan: BilanSynchronisation = { envoyees: 0, appliquees: 0, doublons: 0, conflits: 0, rejetees: 0, photosEnvoyees: 0, photosEnEchec: 0, travailTelecharge: false, erreur: null, ignoree: null };
    if (!this.enLigne()) {
      bilan.ignoree = 'hors_ligne';
      await this.rafraichirEtat();
      return bilan;
    }
    this.changer({ enCours: true, phase: 'envoi_operations', derniereErreur: null, reconnexionRequise: false, enLigne: true });
    let reconnexion = false;
    try {
      const api = this.api();
      await this.envoyerOperations(api, bilan);
      this.changer({ phase: 'envoi_photos' });
      await this.envoyerPhotos(api, bilan);
      if (options.telecharger !== false) {
        this.changer({ phase: 'telechargement' });
        await this.telechargerTravail();
        bilan.travailTelecharge = true;
      }
    } catch (e) {
      bilan.erreur = messageErreur(e);
      if (e instanceof ErreurApi && e.code === 'session_expiree') reconnexion = true;
    } finally {
      this.changer({ enCours: false, phase: 'repos', derniereErreur: bilan.erreur, reconnexionRequise: reconnexion });
      await this.rafraichirEtat();
    }
    return bilan;
  }

  private async envoyerOperations(api: ApiTerrain, bilan: BilanSynchronisation): Promise<void> {
    const enAttente = await listerOperations({ etats: ['en_attente', 'en_cours'] });
    for (let debut = 0; debut < enAttente.length; debut += this.taillePaquet) {
      const paquet = enAttente.slice(debut, debut + this.taillePaquet);
      for (const op of paquet) await mettreAJourOperation(op.id_client, { etat: 'en_cours', tentatives: op.tentatives + 1 });
      let reponse;
      try {
        reponse = await api.synchroniser({ operations: paquet.map(depouiller) });
      } catch (e) {
        const message = messageErreur(e);
        for (const op of paquet) await mettreAJourOperation(op.id_client, { etat: 'en_attente', derniere_erreur: message });
        throw e;
      }
      const maintenant = this.horloge().toISOString();
      const recus = new Set<string>();
      for (const r of reponse.resultats) {
        const op = paquet.find((o) => o.id_client === r.id_client);
        if (!op) continue;
        recus.add(op.id_client);
        bilan.envoyees += 1;
        if (r.resultat === 'appliquee') bilan.appliquees += 1;
        else if (r.resultat === 'ignoree_doublon') bilan.doublons += 1;
        else if (r.resultat === 'conflit') bilan.conflits += 1;
        else bilan.rejetees += 1;
        await mettreAJourOperation(op.id_client, { etat: r.resultat, resultat: r, synchronisee_le: maintenant, derniere_erreur: null });
        if ((r.resultat === 'appliquee' || r.resultat === 'ignoree_doublon') && typeof r.version === 'number') await appliquerVersion(op.objet_type, op.objet_id, r.version);
        if (r.resultat === 'rejetee') {
          for (const p of await listerPhotos(op.id_client)) await mettreAJourPhoto(p.id_client, { etat: 'echec', derniere_erreur: `Opération rejetée : ${r.motif ?? 'sans motif'}` });
        }
      }
      // Une opération sans résultat dans la réponse reste en attente et sera renvoyée (doublon côté serveur au pire).
      for (const op of paquet) if (!recus.has(op.id_client)) await mettreAJourOperation(op.id_client, { etat: 'en_attente', derniere_erreur: 'Aucun résultat renvoyé par le serveur' });
      await ecrireHorodatages({ dernier_contact_serveur: maintenant, derniere_synchronisation: maintenant });
      this.changer({ revision: this.courant.revision + 1 });
    }
  }

  /** Photos une par requête, après l'opération qui les référence ; une erreur fonctionnelle n'arrête pas les autres. */
  private async envoyerPhotos(api: ApiTerrain, bilan: BilanSynchronisation): Promise<void> {
    const photos = (await listerPhotos()).filter((p) => p.etat !== 'envoyee');
    for (const p of photos) {
      const op = await lireOperation(p.operation_id_client);
      if (!op || !ETATS_RECUS.has(op.etat)) continue;
      try {
        const r = await api.envoyerPhoto({ id_client: p.id_client, objet_type: p.objet_type, objet_id_client: p.objet_id_client, empreinte_sha256: p.empreinte, fichier: blobPhoto(p), nom: nomFichierPhoto(p.id_client) });
        await mettreAJourPhoto(p.id_client, { etat: 'envoyee', document_id: r.document_id, derniere_erreur: null, tentatives: p.tentatives + 1 });
        bilan.photosEnvoyees += 1;
      } catch (e) {
        await mettreAJourPhoto(p.id_client, { etat: 'echec', tentatives: p.tentatives + 1, derniere_erreur: messageErreur(e) });
        bilan.photosEnEchec += 1;
        if (e instanceof ErreurApi && (e.horsLigne || e.code === 'session_expiree')) throw e;
      }
      this.changer({ revision: this.courant.revision + 1 });
    }
  }

  /** Télécharge tout le travail de l'agent (pages suivies) ; hors production, une route absente bascule sur le simulateur. */
  async telechargerTravail(): Promise<TravailAppareil> {
    const api = this.api();
    try {
      const t = await travailComplet(api);
      await enregistrerTravail(t, this.horloge());
      return t;
    } catch (e) {
      if (api.source === 'serveur' && e instanceof ErreurApi && e.routeIndisponible && basculeAutomatiquePermise()) {
        activerSimulateur(true);
        const t = await travailComplet(simulateurPartage());
        await enregistrerTravail(t, this.horloge());
        this.changer({ source: 'simulateur' });
        return t;
      }
      throw e;
    }
  }

  /** Repli automatique : retour du réseau, retour au premier plan, toutes les cinq minutes. */
  demarrerAutomatique(intervalleMs = 5 * 60_000): () => void {
    const surLigne = () => {
      this.changer({ enLigne: true });
      void this.synchroniser().catch(() => undefined);
    };
    const horsLigne = () => this.changer({ enLigne: false });
    const surVisibilite = () => {
      if (document.visibilityState === 'visible' && this.enLigne()) void this.synchroniser({ telecharger: false }).catch(() => undefined);
    };
    window.addEventListener('online', surLigne);
    window.addEventListener('offline', horsLigne);
    document.addEventListener('visibilitychange', surVisibilite);
    const minuterie = window.setInterval(() => {
      if (this.enLigne()) void this.synchroniser().catch(() => undefined);
    }, intervalleMs);
    return () => {
      window.removeEventListener('online', surLigne);
      window.removeEventListener('offline', horsLigne);
      document.removeEventListener('visibilitychange', surVisibilite);
      window.clearInterval(minuterie);
    };
  }
}

let partage: Synchroniseur | null = null;

export function synchroniseurPartage(): Synchroniseur {
  if (!partage) partage = new Synchroniseur();
  return partage;
}
